<template>
  <div>
    <v-navigation-drawer
      app
      class="dont-print-header pw-drawer"
      v-model="drawer"
      width="272"
      color="#ffffff"
    >
      <!-- Brand -->
      <div class="pw-brand">
        <v-img
          alt="Pet Walkies"
          contain
          :src="
            company.logo_path
              ? `${mediaUrl + company.logo_path}`
              : require('../../assets/petwalkie-logo.png')
          "
          class="pw-brand__logo"
        />
      </div>

      <!-- Signed-in user -->
      <router-link :to="`/profile/${encryptIt(user.id)}`" class="pw-user">
        <v-avatar size="34" color="primary" class="pw-user__avatar">
          <span class="white--text text-caption font-weight-bold">{{
            userInitials
          }}</span>
        </v-avatar>
        <div class="pw-user__text">
          <div class="pw-user__name">
            {{
              user.first_name != null
                ? user.first_name + " " + user.last_name
                : user.email
            }}
          </div>
        </div>
      </router-link>

      <v-list nav dense class="pw-nav">
        <!-- ============ Overview ============ -->
        <v-list-item to="/home">
          <v-list-item-icon>
            <v-icon size="20">mdi-view-dashboard-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Dashboard</v-list-item-title>
        </v-list-item>

        <!-- Roles on the admin dashboard lose the home-page tiles, so they
             get them here -->
        <v-list-item
          to="/shortcuts"
          v-if="
            company.id != null &&
            visible_to([
              'ADMIN',
              'SUPER ADMIN',
              'BACKEND ADMIN',
              'MASTER FRANCHISEE',
              'FRANCHISEE ADMIN',
            ])
          "
        >
          <v-list-item-icon>
            <v-icon size="20">mdi-view-grid-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Shortcut Dashboard</v-list-item-title>
        </v-list-item>

        <v-list-item
          :to="`/companies`"
          v-if="roleName == 'SUPER ADMIN' && company.id == null"
        >
          <v-list-item-icon>
            <v-icon size="20">mdi-domain</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Companies</v-list-item-title>
        </v-list-item>

        <!-- Each item shows when the user's position has that module (set in
             Configuration > Job Positions); admins see all of them -->

        <!-- ============ Reports ============ -->
        <div class="pw-nav__label" v-if="canAccessAny(['reports', 'action_dashboard'])">
          Reports
        </div>

        <!-- <v-list-item :to="`/reports/dashboard`" v-if="canAccess('reports')">
          <v-list-item-icon>
            <v-icon size="20">mdi-chart-box-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Performance Dashboard</v-list-item-title>
        </v-list-item> -->

        <v-list-item :to="`/action-dashboard`" v-if="canAccess('action_dashboard')">
          <v-list-item-icon>
            <v-icon size="20">mdi-lightning-bolt-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Action Dashboard</v-list-item-title>
        </v-list-item>

        <v-list-item to="/revenue-share" v-if="company.id != null && visible_to(['SUPER ADMIN', 'BACKEND ADMIN'])">
          <v-list-item-icon>
            <v-icon size="20">mdi-chart-donut</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Revenue Share</v-list-item-title>
        </v-list-item>

        <v-list-item :to="`/reports`" exact v-if="canAccess('reports')">
          <v-list-item-icon>
            <v-icon size="20">mdi-file-chart-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>All Reports</v-list-item-title>
        </v-list-item>

        <!-- ============ Bookings & Schedule ============ -->
        <div class="pw-nav__label" v-if="canAccessAny(['monthly_bookings', 'payments', 'walk_reschedule_requests', 'leave_requests'])">
          Bookings
        </div>

        <v-list-item :to="`/user-monthly-job-requests`" v-if="canAccess('monthly_bookings')">
          <v-list-item-icon>
            <v-icon size="20">mdi-calendar-month-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Monthly Bookings</v-list-item-title>
        </v-list-item>

        <v-list-item :to="`/payments`" v-if="canAccess('payments')">
          <v-list-item-icon>
            <v-icon size="20">mdi-receipt-text-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Payments & Invoices</v-list-item-title>
        </v-list-item>

        <v-list-item :to="`/customer-walk-requests`" v-if="canAccess('walk_reschedule_requests')">
          <v-list-item-icon>
            <v-icon size="20">mdi-calendar-refresh-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Walk Reschedule Requests</v-list-item-title>
        </v-list-item>

        <v-list-item :to="`/leave-requests`" v-if="canAccess('leave_requests')">
          <v-list-item-icon>
            <v-icon size="20">mdi-calendar-remove-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Staff Leave Requests</v-list-item-title>
        </v-list-item>

        <!-- ============ People & Network ============ -->
        <div class="pw-nav__label" v-if="canAccessAny(['users', 'master_franchisees', 'franchisees', 'franchisee_staffs', 'my_staff'])">
          People &amp; Network
        </div>

        <v-list-item :to="`/users`" v-if="canAccess('users')">
          <v-list-item-icon>
            <v-icon size="20">mdi-account-group-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Users</v-list-item-title>
        </v-list-item>

        <v-list-item :to="`/master-franchisees`" v-if="canAccess('master_franchisees')">
          <v-list-item-icon>
            <v-icon size="20">mdi-crown-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Master Franchises</v-list-item-title>
        </v-list-item>

        <v-list-item :to="`/franchisees`" v-if="canAccess('franchisees')">
          <v-list-item-icon>
            <v-icon size="20">mdi-storefront-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Franchises</v-list-item-title>
        </v-list-item>

        <v-list-item :to="`/franchisee-staffs`" v-if="canAccess('franchisee_staffs')">
          <v-list-item-icon>
            <v-icon size="20">mdi-account-tie-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Franchise Staff</v-list-item-title>
        </v-list-item>

        <v-list-item :to="`/franchisee-staffs?franchisee_id=${encryptIt(user.franchisee_id)}`" v-if="canAccess('my_staff')">
          <v-list-item-icon>
            <v-icon size="20">mdi-account-tie-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>My Staff</v-list-item-title>
        </v-list-item>

        <!-- ============ Applications ============ -->
        <div class="pw-nav__label" v-if="canAccessAny(['career_applications', 'dog_walker_requests', 'pet_trainer_requests', 'daycare_requests', 'franchise_owner_requests'])">
          Applications
        </div>

        <v-list-item :to="`/career-applications`" v-if="canAccess('career_applications')">
          <v-list-item-icon>
            <v-icon size="20">mdi-briefcase-account-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Job Applications</v-list-item-title>
        </v-list-item>

        <v-list-group
          no-action
          :value="false"
          v-if="canAccessAny(['dog_walker_requests', 'pet_trainer_requests', 'daycare_requests', 'franchise_owner_requests'])"
        >
          <template v-slot:activator>
            <v-list-item-icon>
              <v-icon size="20">mdi-file-account-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Partner Sign-ups</v-list-item-title>
          </template>

          <v-list-item :to="`/pet-walkie-career-requests`" v-if="canAccess('dog_walker_requests')">
            <v-list-item-icon>
              <v-icon size="19">mdi-dog-side</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Dog Walkers</v-list-item-title>
          </v-list-item>

          <v-list-item :to="`/pet-trainer-career-requests`" v-if="canAccess('pet_trainer_requests')">
            <v-list-item-icon>
              <v-icon size="19">mdi-whistle-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Pet Trainers</v-list-item-title>
          </v-list-item>

          <v-list-item :to="`/daycare-career-requests`" v-if="canAccess('daycare_requests')">
            <v-list-item-icon>
              <v-icon size="19">mdi-home-heart</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Daycare Partners</v-list-item-title>
          </v-list-item>

          <v-list-item :to="`/franchisee-career-requests`" v-if="canAccess('franchise_owner_requests')">
            <v-list-item-icon>
              <v-icon size="19">mdi-store-plus-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Franchise Owners</v-list-item-title>
          </v-list-item>
        </v-list-group>

        <!-- ============ Website Content ============ -->
        <div class="pw-nav__label" v-if="canAccessAny(['blogs', 'banners', 'upcoming_services', 'contact_messages'])">
          Website Content
        </div>

        <v-list-item :to="`/blogs`" v-if="canAccess('blogs')">
          <v-list-item-icon>
            <v-icon size="20">mdi-post-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Blog Posts</v-list-item-title>
        </v-list-item>

        <v-list-item :to="`/banners`" v-if="canAccess('banners')">
          <v-list-item-icon>
            <v-icon size="20">mdi-image-multiple-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>App Banners</v-list-item-title>
        </v-list-item>

        <v-list-item :to="`/upcoming-services`" v-if="canAccess('upcoming_services')">
          <v-list-item-icon>
            <v-icon size="20">mdi-star-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Upcoming Services</v-list-item-title>
        </v-list-item>

        <v-list-item :to="`/contact-messages`" v-if="canAccess('contact_messages')">
          <v-list-item-icon>
            <v-icon size="20">mdi-email-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Contact Enquiries</v-list-item-title>
        </v-list-item>

        <!-- ============ Setup ============ -->
        <div class="pw-nav__label" v-if="canAccessAny(['plans', 'coupons', 'reward_settings', 'versions', 'permissions', 'positions', 'state_holidays', 'state_gsts', 'values', 'value_lists'])">
          Setup
        </div>

        <!-- Opens by itself on the plan and zone commission pages -->
        <v-list-group no-action group="plans|zone-commission" v-if="canAccess('plans')">
          <template v-slot:activator>
            <v-list-item-icon>
              <v-icon size="20">mdi-package-variant-closed</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Plans & Commission</v-list-item-title>
          </template>

          <v-list-item :to="`/plans`">
            <v-list-item-icon>
              <v-icon size="19">mdi-format-list-bulleted</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Subscription Plans</v-list-item-title>
          </v-list-item>

          <v-list-item :to="`/zone-commission-settings`">
            <v-list-item-icon>
              <v-icon size="19">mdi-chart-donut</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Zone Commission Settings</v-list-item-title>
          </v-list-item>
        </v-list-group>

        <v-list-item :to="`/coupons`" v-if="canAccess('coupons')">
          <v-list-item-icon>
            <v-icon size="20">mdi-ticket-percent-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Discount Coupons</v-list-item-title>
        </v-list-item>

        <v-list-item :to="`/reward-settings`" v-if="canAccess('reward_settings')">
          <v-list-item-icon>
            <v-icon size="20">mdi-gift-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Reward Settings</v-list-item-title>
        </v-list-item>

        <v-list-item :to="`/versions`" v-if="canAccess('versions')">
          <v-list-item-icon>
            <v-icon size="20">mdi-cellphone-arrow-down</v-icon>
          </v-list-item-icon>
          <v-list-item-title>App Version Control</v-list-item-title>
        </v-list-item>

        <v-list-group
          no-action
          :value="false"
          v-if="canAccessAny(['permissions', 'positions', 'state_holidays', 'state_gsts', 'values', 'value_lists'])"
        >
          <template v-slot:activator>
            <v-list-item-icon>
              <v-icon size="20">mdi-cog-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Configuration</v-list-item-title>
          </template>

          <v-list-item :to="`/permissions`" v-if="canAccess('permissions')">
            <v-list-item-icon>
              <v-icon size="19">mdi-shield-key-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Roles &amp; Permissions</v-list-item-title>
          </v-list-item>

          <v-list-item :to="`/positions`" v-if="canAccess('positions')">
            <v-list-item-icon>
              <v-icon size="19">mdi-badge-account-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Job Positions</v-list-item-title>
          </v-list-item>

          <v-list-item :to="`/state-holidays`" v-if="canAccess('state_holidays')">
            <v-list-item-icon>
              <v-icon size="19">mdi-calendar-star</v-icon>
            </v-list-item-icon>
            <v-list-item-title>State Holidays</v-list-item-title>
          </v-list-item>

          <v-list-item :to="`/state-gsts`" v-if="canAccess('state_gsts')">
            <v-list-item-icon>
              <v-icon size="19">mdi-percent-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>State GST Rates</v-list-item-title>
          </v-list-item>

          <v-list-item :to="`/values`" v-if="canAccess('values')">
            <v-list-item-icon>
              <v-icon size="19">mdi-shape-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Master Categories</v-list-item-title>
          </v-list-item>

          <v-list-item :to="`/value-lists`" v-if="canAccess('value_lists')">
            <v-list-item-icon>
              <v-icon size="19">mdi-format-list-bulleted</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Master Values</v-list-item-title>
          </v-list-item>
        </v-list-group>

        <!-- ============ My Franchise (franchisee admin) ============ -->
        <div class="pw-nav__label" v-if="canAccessAny(['franchise_details', 'kyc_documents'])">
          My Franchise
        </div>

        <v-list-item :to="`/franchisees/${encryptIt(user.franchisee_id)}`" v-if="canAccess('franchise_details')">
          <v-list-item-icon>
            <v-icon size="20">mdi-storefront-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Franchise Details</v-list-item-title>
        </v-list-item>

        <v-list-item :to="`/franchisee-kyc-documents?franchisee_id=${encryptIt(user.franchisee_id)}`" v-if="canAccess('kyc_documents')">
          <v-list-item-icon>
            <v-icon size="20">mdi-file-document-check-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>KYC Documents</v-list-item-title>
        </v-list-item>

        <!-- ============ System ============ -->
        <div class="pw-nav__label" v-if="canAccessAny(['company_profile', 'activity_log'])">
          System
        </div>

        <v-list-item :to="`/companies/${encryptIt(company.id)}`" v-if="canAccess('company_profile')">
          <v-list-item-icon>
            <v-icon size="20">mdi-office-building-cog-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Company Profile</v-list-item-title>
        </v-list-item>

        <v-list-item :to="`/user-timestamps`" v-if="canAccess('activity_log')">
          <v-list-item-icon>
            <v-icon size="20">mdi-history</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Activity Log</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <!-- ============ Top bar ============ -->
    <v-app-bar
      app
      flat
      height="62"
      color="white"
      class="dont-print-header pw-appbar"
    >
      <v-app-bar-nav-icon @click.stop="drawer = !drawer"></v-app-bar-nav-icon>

      <div class="pw-appbar__title">
        <div class="pw-appbar__greeting">
          Hello, {{ user.user_name != null ? user.user_name : user.email }}
        </div>
        <div class="pw-appbar__meta" v-if="company.name">
          {{ company.name }}
        </div>
      </div>

      <v-spacer></v-spacer>

      <!-- Backend admin / admin role switch -->
      <v-switch
        v-if="visible_to(['BACKEND ADMIN'])"
        class="pw-appbar__switch mt-5 mr-4"
        inset
        dense
        hide-details
        @change="switch_to(company.id)"
        :loading="isSwitching"
        v-model="is_acting_admin"
        :label="`${is_acting_admin ? 'Admin' : 'Backend Admin'}`"
      ></v-switch>

      <v-menu offset-y left min-width="200">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on">
            <v-avatar size="32" color="primary">
              <span class="white--text text-caption font-weight-bold">{{
                userInitials
              }}</span>
            </v-avatar>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item
            :to="`/profile/${encryptIt(user.id)}`"
            @click="hide_bell = true"
          >
            <v-list-item-icon class="mr-3">
              <v-icon size="19">mdi-account-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>My Profile</v-list-item-title>
          </v-list-item>
          <v-list-item to="/notifications" @click="hide_bell = true">
            <v-list-item-icon class="mr-3">
              <v-icon size="19">mdi-bell-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>Notifications</v-list-item-title>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item @click="logout">
            <v-list-item-icon class="mr-3">
              <v-icon size="19" color="error">mdi-logout</v-icon>
            </v-list-item-icon>
            <v-list-item-title class="error--text">Sign out</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <router-view />
    <!-- Clear Success Dialog Box -->
    <v-dialog v-model="show_company_dialog" width="850">
      <v-card rounded="xxl">
        <v-card-title> <h1>Switch to Admin</h1> </v-card-title>
        <v-card-subtitle class="my-1">
          Please select the company whose admin portal you'd like to switch to.
        </v-card-subtitle>
        <v-card-text>
          <v-row>
            <v-col md="6">
              <v-text-field
                solo
                rounded
                v-model="form.search_keyword"
                label="Search ... "
                clearable
                color="#e60040"
                @click:clear="(form.search_keyword = null), getCompanies()"
                prepend-inner-icon="mdi-magnify"
                @keydown.enter="getCompanies"
                @change="page = 1"
              >
              </v-text-field>
            </v-col>
          </v-row>
          <v-progress-linear
            v-if="isLoading"
            indeterminate
            color="primary"
          ></v-progress-linear>
          <v-row v-else>
            <v-col md="12">
              <v-simple-table fixed-header height="285" class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">Status</th>
                      <th class="text-left" style="min-width: none">Name</th>
                      <th class="text-left">Email</th>
                      <th class="text-left">Admin Name</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="companies.length != 0">
                    <tr
                      v-for="(company, i) in companies"
                      :key="`company_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                        'table-row-last': companies.length - 1 == i,
                      }"
                    >
                      <td style="min-width: 85px">
                        {{ i + 1 }}
                      </td>
                      <td>
                        <v-tooltip bottom v-if="company.is_active == 1">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="teal"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15"
                                  >mdi-checkbox-marked-circle</v-icon
                                >
                              </v-avatar>
                              Active
                            </v-chip>
                          </template>
                          <span>Company is Active</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="company.is_active == 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              small
                              color="red"
                              text-color="white"
                            >
                              <v-avatar left>
                                <v-icon size="15">mdi-cross-circle</v-icon>
                              </v-avatar>
                              Inactive
                            </v-chip>
                          </template>
                          <span>Company is InActive</span>
                        </v-tooltip>
                      </td>
                      <td>
                        <v-avatar rounded>
                          <v-img
                            alt="Vuetify Name"
                            class="mt-1"
                            contain
                            min-width="35"
                            :src="
                              company.logo_path
                                ? `${mediaUrl + company.logo_path}`
                                : require('../../assets/petwalkie-logo.png')
                            "
                            width="35"
                            height="35"
                          />
                        </v-avatar>
                        &nbsp;
                        {{ company.name || "" }}
                      </td>
                      <td>{{ company.email || "" }}</td>
                      <td>{{ company.admin_name || "" }}</td>
                      <td class="text-right">
                        <v-btn
                          class="ml-2"
                          fab
                          x-small
                          color="primary"
                          @click="changeCompany(company.id)"
                        >
                          <v-icon dark> mdi-account-switch-outline </v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="7" class="text-center">No data found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
    <unauthorised-pop-up />
    <GlobalPopUP ref="globalModal" />

    <!-- Clear Success Dialog Box -->
    <v-dialog v-model="show_authorised_warning_dialog" width="850">
      <v-card rounded="xxl">
        <v-card-title> <h1>Warning</h1> </v-card-title>
        <v-card-subtitle class="my-1">
          you are trying to access content that are not accessable through your
          login. kindly ask your admin regarding same.
        </v-card-subtitle>
      </v-card>
    </v-dialog>
  </div>
</template>

<script type="text/javascript">
import { mapActions } from "vuex";
import axios from "axios";
import store from "@/store";
import UnauthorisedPopUp from "@/components/UnauthorisedPopUp.vue";
import GlobalPopUP from "@/components/globalPopUP.vue";

export default {
  components: { UnauthorisedPopUp, GlobalPopUP },
  data() {
    return {
      form: {
        search_keyword: "",
      },
      hide_bell: false,
      drawer: true,
      show_authorised_warning_dialog: false,
      is_acting_admin: false,
      companyName: "",
      selectedCompanyId: "",
      company_data: {},
      companies: [],
      is_company_user: false,
      isLoading: false,
      isSwitching: false,
      show_company_dialog: false,
    };
  },
  computed: {
    userInitials() {
      const first = (this.user.first_name || "").trim();
      const last = (this.user.last_name || "").trim();
      if (first || last) {
        return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
      }
      return (this.user.email || "?").charAt(0).toUpperCase();
    },
  },
  mounted() {
    let company = store.getters["auth/company"];
    if (Object.keys(company).length > 0) {
      this.selectedCompanyId = company.id;
      if (this.roleName == "BACKEND ADMIN") {
        this.is_acting_admin = true;
        console.log(this.is_acting_admin);
      }
      this.is_company_user = company ? true : false;
    }
  },
  methods: {
    ...mapActions({
      logOut: "auth/logOut",
    }),
    logout() {
      this.logOut();
      location.replace("/");
    },
    isActingAdmin() {
      let response = false;
      if (this.roleName == "BACKEND ADMIN" && this.company.id != null) {
        response = true;
      }
      return response;
    },
    // Scroll back to the 1st validation error Message
    scrollToSection(selector) {
      // Example: smooth scroll to the specific section
      const element = document.querySelector(selector);
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = absoluteElementTop - window.innerHeight / 2;
      window.scrollTo({
        top: middle,
        behavior: "smooth",
      });
      console.log(selector);
    },
    async getCompanies() {
      this.isLoading = true;
      this.form.search_keyword = this.form.search_keyword
        ? this.form.search_keyword
        : "";
      this.form.is_active = this.form.is_active ? this.form.is_active : "";
      let companies = [];
      let query =
        "search_keyword=" +
        this.form.search_keyword +
        "&is_active=" +
        this.form.is_active;
      companies = await axios.get(`companies?${query}`);
      this.companies = companies.data.data;
      this.count = companies.data.count;
      this.isLoading = false;
    },
    changeCompany(companyId) {
      this.selectedCompanyId = companyId;
      let company = this.companies.find((s) => s.id == this.selectedCompanyId);
      if (company) {
        console.log(company);
        store.dispatch("auth/setCompany", company);
        this.is_company_user = true;
      } else {
        store.dispatch("auth/setCompany", "");
        this.is_company_user = false;
      }
      window.location.href = "/auth/login";
      this.isSwitching = false;
      this.show_company_dialog = false;
      // this.$router.push("/auth/login");
    },
    switch_to(companyId = "") {
      this.isSwitching = true;
      console.log(this.is_acting_admin);
      if (this.is_acting_admin == true) {
        console.log("CompanyID " + companyId);
        console.log("Already SuperAdmin Switching to Admin");
        this.show_company_dialog = true;
      } else {
        console.log("Already Acting Admin Switching to SuperAdmin");
        this.selectedCompanyId = null;
        this.changeCompany();
      }
    },
  },
};
</script>

<style scoped>
/* ---------- Sidebar ---------- */
.pw-drawer {
  border-right: 1px solid #e4e8ee !important;
}

.pw-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 16px 14px;
  border-bottom: 1px solid #eef1f5;
}

.pw-brand__logo {
  border-radius: 8px;
  width: 100% !important;
  max-height: 60px;
}

.pw-brand__name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #1a2436;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.pw-brand__sub {
  font-size: 0.6875rem;
  color: #8d99ad;
  font-weight: 500;
}

.pw-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin: 10px 12px 4px;
  border-radius: 12px;
  background: #f6f8fb;
  text-decoration: none;
  transition: background 0.15s ease;
}

.pw-user:hover {
  background: #eef3fb;
}

.pw-user__text {
  min-width: 0;
}

.pw-user__name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1a2436;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pw-user__role {
  font-size: 0.6875rem;
  color: #66738a;
  text-transform: capitalize;
}

/* ---------- Nav list ---------- */
.pw-nav {
  padding: 4px 12px 24px;
}

.pw-nav__label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #9aa5b7;
  padding: 18px 12px 6px;
}

::v-deep .pw-nav .v-list-item {
  min-height: 38px;
  border-radius: 10px;
  margin-bottom: 2px;
  color: #46536b;
}

::v-deep .pw-nav .v-list-item__title {
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  line-height: 1.3;
}

::v-deep .pw-nav .v-list-item__icon {
  margin: 7px 12px 7px 0 !important;
  align-self: center;
}

::v-deep .pw-nav .v-list-item:hover {
  background: #f2f5f9;
}

::v-deep .pw-nav .v-list-item--active {
  background: #eef3fb !important;
  color: var(--v-primary-base) !important;
}

::v-deep .pw-nav .v-list-item--active .v-list-item__title {
  font-weight: 600;
}

::v-deep .pw-nav .v-list-group__header {
  border-radius: 10px;
  min-height: 38px;
}

::v-deep .pw-nav .v-list-group--active > .v-list-group__header {
  background: transparent;
}

.v-application--is-ltr
  .v-list--dense.v-list--nav
  .v-list-group--no-action
  > .v-list-group__items
  > .v-list-item {
  padding: 0 8px 0 18px;
}

/* ---------- Top bar ---------- */
.pw-appbar {
  border-bottom: 1px solid #e4e8ee !important;
}

.pw-appbar__title {
  margin-left: 4px;
  min-width: 0;
}

.pw-appbar__greeting {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1a2436;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pw-appbar__meta {
  font-size: 0.75rem;
  color: #8d99ad;
  line-height: 1.2;
}

::v-deep .pw-appbar__switch label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #66738a;
}
</style>
